import { Sprite } from 'pixi.js';
import { IsoScene } from '@enugu/engine';
import { applyAreaMapToGrid } from '@enugu/shared';
import { rng } from '../art/paint';
/**
 * M0 test map: a Coal Camp-like corner of Enugu (64 x 48 tiles).
 *
 *   y  0..4   backyard: bathrooms, drums, generator, overhead tank
 *   y  5..20  face-me-I-face-you compound (x 13..24): 8 rooms on a central corridor
 *   y 21..23  front yard: mama-put bench, parked keke
 *   y 24, 31  open gutters (crossable only on slabs)
 *   y 25..30  the street (kerb, asphalt)
 *   y 32..47  edge of the market: zinc-roof stalls, POS umbrellas
 *   x 36..47, y 7..16: a bungalow; trees and laterite everywhere else
 *
 * Coordinates: +x runs screen down-right (SE), +y screen down-left (SW).
 */
export const MAP_W = 64;
export const MAP_H = 48;
const GUTTER_ROWS = [24, 31];
const SLAB_X = new Set([8, 18, 19, 40, 52]);
function floorKey(x, y, r) {
    const n = (k) => Math.floor(r * k);
    if (GUTTER_ROWS.includes(y))
        return SLAB_X.has(x) ? 'floor/slab/0' : 'floor/gutter/0';
    if (y === 25 || y === 30)
        return 'floor/kerb/0';
    if (y > 25 && y < 30)
        return `floor/asphalt/${n(3)}`;
    if (x >= 13 && x < 25 && y >= 5 && y < 21) {
        if (x === 18 || x === 19)
            return `floor/cement/${n(2)}`; // corridor
        return 'floor/room/0';
    }
    if (x >= 37 && x < 47 && y >= 8 && y < 16)
        return 'floor/room/0';
    if (x >= 12 && x < 26 && y >= 0 && y < 24)
        return `floor/cement/${n(2)}`; // yards
    if (y >= 32 && x >= 29)
        return `floor/market/${n(2)}`;
    if ((x - 6) ** 2 + (y - 40) ** 2 < 30 || (x - 4) ** 2 + (y - 8) ** 2 < 14)
        return `floor/grass/${n(2)}`;
    return `floor/laterite/${n(4)}`;
}
/**
 * Builds Coal Camp. With `map` (a parsed area map, M1-09) the walk grid and
 * spawn come from the map; props, walls art, decals and lights stay procedural.
 * Without it this is M0's builder, which `content/maps` uses to generate the JSON.
 */
export function buildCoalCamp(engine, dollFrames, seed = 7, map = null) {
    const assets = engine.assets;
    const tex = (k) => assets.texture(k);
    const floorRand = rng(seed);
    const floorRolls = new Float32Array(MAP_W * MAP_H).map(() => floorRand());
    const scene = new IsoScene({
        width: MAP_W,
        height: MAP_H,
        texture: tex,
        dollFrames,
        floor: (x, y) => tex(floorKey(x, y, floorRolls[y * MAP_W + x])),
    });
    const grid = scene.grid;
    // Gutters are walkable only on the slabs.
    for (const y of GUTTER_ROWS)
        for (let x = 0; x < MAP_W; x++)
            if (!SLAB_X.has(x))
                grid.blockFootprint(x, y, 1, 1, true);
    // ---------------------------------------------------------------- compound
    // Rooms must be marked BEFORE walls are added (cutaway is derived from them).
    const rows = [5, 9, 13, 17];
    let roomId = 1;
    for (const y of rows) {
        scene.markRoom(13, y, 5, 4, roomId++);
        scene.markRoom(20, y, 5, 4, roomId++);
    }
    scene.markRoom(18, 5, 2, 16, roomId++); // corridor
    scene.markRoom(37, 8, 5, 8, roomId++); // bungalow
    scene.markRoom(42, 8, 5, 8, roomId++);
    const walls = [];
    const W = (x, y, style, variant) => {
        walls.push(variant ? { x, y, edge: 'W', style, variant } : { x, y, edge: 'W', style });
    };
    const N = (x, y, style, variant) => {
        walls.push(variant ? { x, y, edge: 'N', style, variant } : { x, y, edge: 'N', style });
    };
    for (let x = 13; x < 25; x++) {
        if (x !== 18)
            N(x, 5, 'wall/cream', x === 15 || x === 22 ? 'window' : undefined); // back wall, back door at 18
        if (x !== 18 && x !== 19)
            N(x, 21, 'wall/cream', x === 15 || x === 22 ? 'window' : undefined); // front, entrance 18-19
    }
    for (let y = 5; y < 21; y++) {
        W(13, y, 'wall/cream', (y - 5) % 4 === 2 ? 'window' : undefined);
        W(25, y, 'wall/cream', (y - 5) % 4 === 1 ? 'window' : undefined);
        if (![6, 10, 14, 18].includes(y))
            W(18, y, 'wall/blue');
        if (![7, 11, 15, 19].includes(y))
            W(20, y, 'wall/blue');
    }
    for (const y of [9, 13, 17]) {
        for (let x = 13; x < 18; x++)
            N(x, y, 'wall/pink');
        for (let x = 20; x < 25; x++)
            N(x, y, 'wall/pink');
    }
    // Bungalow: two rooms, door on the street side.
    for (let x = 37; x < 47; x++) {
        N(x, 8, 'wall/blue', x % 3 === 0 ? 'window' : undefined);
        if (x !== 39 && x !== 44)
            N(x, 16, 'wall/blue', x % 3 === 0 ? 'window' : undefined);
    }
    for (let y = 8; y < 16; y++) {
        W(37, y, 'wall/blue', y === 11 ? 'window' : undefined);
        W(47, y, 'wall/blue', y === 12 ? 'window' : undefined);
        if (y !== 12)
            W(42, y, 'wall/pink');
    }
    // Block fence behind the backyard.
    for (let x = 12; x < 26; x++)
        N(x, 0, 'wall/block');
    for (const w of walls)
        scene.addWall(w);
    scene.setWallMode('cutaway');
    // ---------------------------------------------------------------- props
    const prop = (texture, x, y, w, h, height, name) => {
        scene.addProp(name ? { texture, x, y, w, h, height, name } : { texture, x, y, w, h, height });
    };
    const r = rng(seed + 1);
    // Furniture per room; doors (west rooms at x=17, east rooms at x=20) stay clear.
    rows.forEach((y, i) => {
        // West room x 13..17
        prop(i % 2 ? 'prop/mattress' : 'prop/bed', 13, y, 1, 2, 1, 'bed');
        prop('prop/wardrobe', 13, y + 3, 1, 1, 2.3, 'wardrobe');
        prop('prop/table', 15, y, 1, 1, 1, 'table');
        prop(r() < 0.5 ? 'prop/chair-white' : 'prop/chair-red', 16, y, 1, 1, 1.2, 'chair');
        prop(i === 1 ? 'prop/tv' : 'prop/stove', 15, y + 3, 1, 1, 1.2);
        // East room x 20..24
        prop('prop/bed', 24, y, 1, 2, 1, 'bed');
        prop('prop/table', 22, y + 3, 1, 1, 1, 'table');
        prop('prop/stool', 23, y + 3, 1, 1, 0.6);
        prop(i % 2 ? 'prop/drum' : 'prop/tv', 24, y + 3, 1, 1, 1.2);
    });
    // Corridor clutter (leaves a 1-tile lane).
    prop('prop/bucket', 18, 8, 1, 1, 0.6, 'bucket');
    prop('prop/jerrycans', 18, 16, 1, 1, 0.8, 'jerrycans');
    // Backyard.
    prop('prop/bathroom', 13, 1, 1, 1, 2.6, 'bathroom');
    prop('prop/bathroom', 14, 1, 1, 1, 2.6, 'bathroom');
    prop('prop/drum', 16, 2, 1, 1, 1.2, 'water drum');
    prop('prop/drum', 17, 2, 1, 1, 1.2, 'water drum');
    prop('prop/generator', 22, 2, 1, 1, 1, 'generator');
    prop('prop/tank', 23, 1, 2, 2, 3.8, 'overhead tank');
    // Front yard: mama put.
    prop('prop/bench', 13, 22, 3, 1, 0.7, 'bench');
    prop('prop/table', 16, 22, 1, 1, 1);
    prop('prop/stove', 16, 23, 1, 1, 1.2, 'mama put stove');
    prop('prop/keke', 21, 22, 2, 1, 1.8, 'keke');
    prop('prop/chair-red', 23, 23, 1, 1, 1.2);
    // Bungalow furniture.
    prop('prop/bed', 38, 9, 1, 2, 1, 'bed');
    prop('prop/table', 40, 10, 1, 1, 1);
    prop('prop/tv', 45, 9, 1, 1, 1.2);
    prop('prop/chair-white', 43, 13, 1, 1, 1.2);
    prop('prop/chair-white', 44, 13, 1, 1, 1.2);
    // Street lamps on the kerb, trees on open ground.
    for (const x of [10, 30, 50])
        prop('prop/streetlamp', x, 25, 1, 1, 4, 'street lamp');
    for (const [x, y] of [[6, 12], [31, 6], [33, 18], [54, 10], [8, 36], [27, 40], [58, 20], [3, 22]])
        prop('prop/tree', x, y, 1, 1, 4, 'mango tree');
    prop('prop/keke', 44, 28, 2, 1, 1.8, 'keke');
    // Market: rows of stalls with aisles.
    for (const y of [34, 39, 44])
        for (let x = 32; x <= 58; x += 4)
            prop('prop/stall', x, y, 2, 2, 2.6, 'market stall');
    prop('prop/umbrella', 29, 33, 2, 2, 3, 'POS stand');
    prop('prop/umbrella', 29, 42, 2, 2, 3, 'POS stand');
    prop('prop/bench', 4, 28, 3, 1, 0.7, 'bench');
    // Floor decals (no sorting needed: they sit on the floor layer).
    const decals = [
        ['decal/pothole', 12, 27], ['decal/pothole', 37, 28], ['decal/puddle', 22, 26], ['decal/oil', 45, 29],
        ['decal/litter', 34, 37], ['decal/litter', 50, 42], ['decal/mat', 19, 21], ['decal/puddle', 16, 3],
    ];
    for (const [k, x, y] of decals) {
        const sprite = new Sprite(engine.assets.texture(k));
        // Same anchor rule as floor tiles: bottom vertex of the 1x1 footprint.
        sprite.position.set((x - y) * 32, (x + y + 2) * 16);
        scene.floor.decals.addChild(sprite);
    }
    // ---------------------------------------------------------------- lights
    const L = engine.lighting;
    const warm = 0xffd9a0;
    const tube = 0xf4f1e0;
    const gridLamps = [];
    for (const y of [7, 12, 17])
        gridLamps.push(L.addLight({ x: 19, y: y + 0.5, z: 2.6, radius: 4, color: tube, intensity: 0.95 }));
    rows.forEach((y) => {
        gridLamps.push(L.addLight({ x: 15.5, y: y + 2, z: 2.6, radius: 3.2, color: warm, intensity: 0.9 }));
        gridLamps.push(L.addLight({ x: 22.5, y: y + 2, z: 2.6, radius: 3.2, color: warm, intensity: 0.9 }));
    });
    gridLamps.push(L.addLight({ x: 19.5, y: 22.5, z: 2.4, radius: 4.5, color: warm, intensity: 1 }));
    gridLamps.push(L.addLight({ x: 39.5, y: 12, z: 2.6, radius: 3.5, color: warm, intensity: 0.9 }));
    gridLamps.push(L.addLight({ x: 44.5, y: 12, z: 2.6, radius: 3.5, color: tube, intensity: 0.9 }));
    const streetLamps = [10, 30, 50].map((x) => L.addLight({ x: x + 0.5, y: 26.4, z: 3.6, radius: 6, color: 0xffc070, intensity: 1 }));
    // Market traders run their own lanterns: always on, flickering.
    for (const y of [34, 39, 44])
        for (let x = 32; x <= 58; x += 8)
            L.addLight({ x: x + 1, y: y + 2.4, z: 1.2, radius: 3, color: 0xffb060, intensity: 0.85, flicker: 0.25 });
    L.addLight({ x: 30, y: 34, z: 1.5, radius: 3, color: 0xcfe3ff, intensity: 0.8 }); // POS screen glow
    let playerSpawn = { x: 19, y: 12 };
    if (map) {
        // The map is authoritative for walkability: drop the procedural blockers and walls.
        grid.staticBlock.fill(0);
        grid.walls.fill(0);
        applyAreaMapToGrid(map, grid);
        playerSpawn = { x: map.spawn.x, y: map.spawn.y };
    }
    return { scene, gridLamps, streetLamps, playerSpawn, generatorPos: { x: 22.5, y: 2.5 }, map };
}
